import React, { useState } from "react";
import Axios from "../../_services/caller.service";
import styles from "./TestReminders.module.scss";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { PermissionGate } from "../superAdmin/managePermissions/PermissionGate";
import {
  FiCheckCircle,
  FiAlertTriangle,
  FiSend,
  FiTrash2,
  FiLock,
} from "react-icons/fi";

// Onglet "Test" : chaque etape est independante et lancable seule.
const TestReminders = () => {
  const { t } = useTranslation();

  const [loadingStep, setLoadingStep] = useState(null);
  const [results, setResults] = useState({}); // { [step]: data }

  const RECIPIENT = "gribeau@hearing-space.com";

  const markDone = (step, data) => {
    setResults((prev) => ({ ...prev, [step]: data }));
  };

  // Appel generique d'un scenario d'envoi
  const runScenario = async (step, scenario) => {
    setLoadingStep(step);
    try {
      const res = await Axios.post("/test/sendScenario", { scenario });
      markDone(step, res.data);
      if (res.data.sent === res.data.total) {
        toast.success(
          t("calibration_test.emails_sent", {
            count: res.data.sent,
            total: res.data.total,
          })
        );
      } else {
        // Cas partiel ou scenario d'erreur volontaire : on informe sans crier au succes
        toast.info(
          t("calibration_test.emails_sent", {
            count: res.data.sent,
            total: res.data.total,
          })
        );
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error.message;
      toast.error(msg);
      setResults((prev) => ({ ...prev, [step]: { success: false, error: msg } }));
    } finally {
      setLoadingStep(null);
    }
  };

  const runSeedError = async (step) => {
    setLoadingStep(step);
    try {
      const res = await Axios.post("/test/seedErrorReminder");
      markDone(step, res.data);
      toast.success(t("calibration_test.seed_done"));
    } catch (error) {
      const msg = error?.response?.data?.message || error.message;
      toast.error(msg);
    } finally {
      setLoadingStep(null);
    }
  };

  const runCleanupObsolete = async (step) => {
    setLoadingStep(step);
    try {
      const res = await Axios.post("/test/cleanupObsolete");
      markDone(step, res.data);
      if (res.data.existedBefore && !res.data.existsAfter) {
        toast.success(t("calibration_test.cleanup_ok"));
      } else {
        toast.warn(res.data.message);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error.message;
      toast.error(msg);
    } finally {
      setLoadingStep(null);
    }
  };

  const runCleanupTestData = async () => {
    setLoadingStep("cleanup");
    try {
      const res = await Axios.post("/test/cleanupTestData");
      toast.success(res.data.message);
    } catch (error) {
      const msg = error?.response?.data?.message || error.message;
      toast.error(msg);
    } finally {
      setLoadingStep(null);
    }
  };

  // Rendu du tableau de resultats d'envoi
  const renderSendResults = (data) => {
    if (!data?.results) return null;
    return (
      <div className={styles.resultBox}>
        <p className={styles.resultRecipient}>
          {t("calibration_test.recipient")} : <strong>{data.recipient}</strong>
        </p>
        <table className={styles.resultTable}>
          <thead>
            <tr>
              <th>{t("reminders.transducer")}</th>
              <th>Template</th>
              <th>{t("calibration_test.days")}</th>
              <th>{t("reminders.email_status")}</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((r, i) => (
              <tr key={i}>
                <td>{r.transducer}</td>
                <td>{r.templateId}</td>
                <td>{r.days}</td>
                <td>
                  {r.success ? (
                    <span className={styles.ok}>
                      <FiCheckCircle /> {t("common.success")}
                    </span>
                  ) : (
                    <span className={styles.ko}>
                      <FiAlertTriangle /> {r.error || t("common.error")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Definition des etapes
  const steps = [
    {
      n: 1,
      title: t("calibration_test.step1_title"),
      desc: t("calibration_test.step1_desc"),
      action: (
        <button
          className={styles.actionBtn}
          disabled={loadingStep === 1}
          onClick={() => runScenario(1, "echeance_retard")}
        >
          <FiSend /> {t("calibration_test.step1_btn")}
        </button>
      ),
      result: () => renderSendResults(results[1]),
    },
    {
      n: 2,
      title: t("calibration_test.step2_title"),
      desc: t("calibration_test.step2_desc"),
      action: (
        <button
          className={styles.actionBtn}
          disabled={loadingStep === 2}
          onClick={() => runScenario(2, "transducers")}
        >
          <FiSend /> {t("calibration_test.step2_btn")}
        </button>
      ),
      result: () => renderSendResults(results[2]),
    },
    {
      n: 3,
      title: t("calibration_test.step3_title"),
      desc: t("calibration_test.step3_desc"),
      action: (
        <button
          className={styles.actionBtn}
          disabled={loadingStep === 3}
          onClick={() => runScenario(3, "error")}
        >
          <FiAlertTriangle /> {t("calibration_test.step3_btn")}
        </button>
      ),
      result: () => renderSendResults(results[3]),
    },
    {
      n: 4,
      title: t("calibration_test.step4_title"),
      desc: t("calibration_test.step4_desc"),
      action: (
        <button
          className={styles.actionBtn}
          disabled={loadingStep === 4}
          onClick={() => runSeedError(4)}
        >
          <FiSend /> {t("calibration_test.step4_btn")}
        </button>
      ),
      result: () =>
        results[4]?.success ? (
          <div className={styles.resultBox}>
            <p className={styles.hint}>{t("calibration_test.step4_hint")}</p>
          </div>
        ) : null,
    },
    {
      n: 5,
      title: t("calibration_test.step5_title"),
      desc: t("calibration_test.step5_desc"),
      action: (
        <button
          className={styles.actionBtn}
          disabled={loadingStep === 5}
          onClick={() => runCleanupObsolete(5)}
        >
          <FiTrash2 /> {t("calibration_test.step5_btn")}
        </button>
      ),
      result: () =>
        results[5] ? (
          <div className={styles.resultBox}>
            <p>
              {t("calibration_test.existed_before")} :{" "}
              <strong>{String(results[5].existedBefore)}</strong>
            </p>
            <p>
              {t("calibration_test.exists_after")} :{" "}
              <strong>{String(results[5].existsAfter)}</strong>
            </p>
            <p className={results[5].existsAfter ? styles.ko : styles.ok}>
              {results[5].message}
            </p>
          </div>
        ) : null,
    },
  ];

  return (
    <PermissionGate actionKey={"ManageCertificat.remindCalibrationEmail"}>
      <div className={styles.wizard}>
        <div className={styles.intro}>
          <h3>{t("calibration_test.title")}</h3>
          <p className={styles.warn}>
            <FiLock /> {t("calibration_test.safety_note", { email: RECIPIENT })}
          </p>
        </div>

        {steps.map((step) => {
          const done = !!results[step.n];
          return (
            <div
              key={step.n}
              className={`${styles.step} ${done ? styles.done : ""}`}
            >
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>
                  {done ? <FiCheckCircle /> : step.n}
                </span>
                <div>
                  <h4>{step.title}</h4>
                  <p className={styles.desc}>{step.desc}</p>
                </div>
              </div>

              <div className={styles.stepBody}>
                {step.action}
                {step.result()}
              </div>
            </div>
          );
        })}

        <div className={styles.footer}>
          <button
            className={styles.cleanupBtn}
            disabled={loadingStep === "cleanup"}
            onClick={runCleanupTestData}
          >
            <FiTrash2 /> {t("calibration_test.cleanup_btn")}
          </button>
        </div>
      </div>
    </PermissionGate>
  );
};

export default TestReminders;
